
import { RevButton } from '@ds/components/Button'
import { RevCardCarousel } from '@ds/components/CardCarousel'
import { RevContainer } from '@ds/components/Container'
import { RevIllustration } from '@ds/components/Illustration'
import { IconArrowRight } from '@ds/icons/IconArrowRight'
import isEmpty from 'lodash/isEmpty'
import omit from 'lodash/omit'
import { mapGetters } from 'vuex'

import Breadcrumb from '@app/components/Breadcrumb'
import { prepareJsonLdScript } from '@core/helpers'
import { generateAlternateLink } from '@core/helpers/head'
import { tw } from '@core/tailwind'
import { buyingGuideDetailsFetch } from '@http/endpoints'
import SearchParams from '@landing/utils/SearchParams'
import RecommendedProductsBlock from '@recommendation/components/RecommendedProductsBlock.vue'
import { PRODUCT_SOURCE_TYPE } from '@tracking'
import { trackClick } from '@tracking/events'

import Author from '../../components/Author'
import BuyingGuideCard from '../../components/Card'
import Header from '../../components/Header'
import Menu from '../../components/Menu'
import ProductsAlgolia from '../../components/ProductsAlgolia'
import RteParser from '../../components/RteParser'
import Vote from '../../components/Vote'
import { buildBreadcrumb } from '../../helpers'

import {
  CTA_TYPES_FOR_TRACKING,
  DATA_SELECTOR_ATTRIBUTE_FOR_SEO,
} from './LayoutBuyingGuide.constants'
import translations from './LayoutBuyingGuide.translations'

export default {
  components: {
    RevButton,
    Author,
    Breadcrumb,
    BuyingGuideCard,
    RevCardCarousel,
    RevContainer,
    Header,
    RevIllustration,
    Menu,
    ProductsAlgolia,
    RecommendedProductsBlock,
    RteParser,
    Vote,
  },
  async asyncData({ store, params, error, redirect = {} }) {
    const countryCode = store.getters['config/country']

    try {
      const { payload } = await store.dispatch('http/request', {
        request: buyingGuideDetailsFetch,
        pathParams: {
          // TODO EXPLAIN
          slug: params.slug,
        },
      })

      // TODO: Do this check on backend, they have the COUNTRY header
      if (payload.countryCode !== countryCode) {
        return error({
          statusCode: 403,
          message: 'Buying Guide not available in this country',
        })
      }

      if (params.category !== payload.link.params.category) {
        return redirect(301, {
          name: payload.link.name,
          params: { ...payload.link.params },
        })
      }

      let landingId
      const paragraphs = payload.paragraphs.map((paragraph, index) => {
        let currentParagraph = paragraph
        // We get the first landing ID to call the product from algolia
        if (paragraph.ebLanding && !landingId) {
          landingId = paragraph.ebLanding
          // we remove the EB id to hide the first EB block when there is Algolia products
          currentParagraph = omit(paragraph, 'ebLanding')
        }

        return {
          ...currentParagraph,
          anchor: `section-${index + 1}`,
        }
      })

      if (landingId) {
        await store.dispatch('landingSearch/fetchResults', {
          searchParams: new SearchParams({
            query: {},
            hash: '',
            context: {
              source: 'product-landing',
              slug: '',
              id: landingId,
            },
            paramsWhitelist: store.getters['landingSearch/filterWhitelist'],
          }),
        })
      }

      const baseUrl = store.getters['config/baseURL']
      const buildAlternateLinks = Object.values(payload.alternateLinks).map(
        ({ country, link }) => {
          return generateAlternateLink({ link, country, baseUrl })
        },
      )

      const alternateLinks = await Promise.all(buildAlternateLinks)

      // TODO: Build the right shape on the backend directly?
      // Or transformResponse in the meantime?
      return {
        alternateLinks,
        breadcrumb: buildBreadcrumb(payload.breadcrumb),
        title: payload.title,
        summary: payload.summary,
        banner: {
          image: payload.banner,
          retina: payload.bannerRetina,
          alt: payload.bannerAlt,
        },
        author: {
          quote: payload.author.text,
          image: payload.author.image,
          alt: payload.author.imageAlt,
          name: payload.author.name,
          position: payload.author.position,
        },
        vote: {
          upVotes: payload.upVotes,
          downVotes: payload.downVotes,
        },
        menu: paragraphs.map(({ anchor, title }) => ({
          id: anchor,
          title,
        })),
        paragraphs,
        relatedArticles: payload.relatedBuyingGuides,
        category: payload.rootCategory,
        slug: payload.slug,
        seo: {
          title: payload.titleSeo,
          description: payload.metaDesc,
          jsonLds: payload.jsonLds,
        },
      }
    } catch (err) {
      const statusCode = err.status_code || 404
      const message =
        err.message || `Details not found for Buying Guide ${params.slug}`

      return error({
        statusCode,
        message,
      })
    }
  },
  data() {
    return {
      alternateLinks: [],
      breadcrumb: {},
      author: {},
      menu: [],
      paragraphs: [],
      relatedArticles: [],
      mainHeaderHeight: 65,
      seo: {
        title: '',
        description: '',
        jsonLds: [],
      },
      vote: {
        upVotes: 0,
        downVotes: 0,
      },
      title: '',
      summary: '',
      banner: {},
      category: '',
      slug: '',
    }
  },
  head() {
    const jsonLds = this.seo.jsonLds.reduce((acc, json, index) => {
      const [key] = Object.keys(json)

      return {
        ...acc,
        [index]: json[key],
      }
    }, {})

    const canonicalLink = {
      rel: 'canonical',
      href: `${this.baseURL}${this.$route.path}`,
    }

    return {
      link: [...this.alternateLinks, canonicalLink],
      title: this.seo.title,
      meta: [
        {
          hid: 'description',
          name: 'description',
          content: this.seo.description,
        },
        {
          hid: 'og:title',
          property: 'og:title',
          content: this.title,
        },
        {
          hid: 'og:description',
          property: 'og:description',
          content: this.seo.description,
        },
        {
          hid: 'twitter:title',
          property: 'twitter:title',
          content: this.title,
        },
        {
          property: 'twitter:description',
          hid: 'twitter:description',
          content: this.seo.description,
        },
      ],
      script: prepareJsonLdScript(jsonLds),
      __dangerouslyDisableSanitizers: ['script'],
    }
  },
  computed: {
    ...mapGetters({
      baseURL: 'config/baseURL',
      country: 'config/country',
      searchResults: 'landingSearch/results',
    }),
    list: () => PRODUCT_SOURCE_TYPE.HIGHLIGHTS,
    icon: () => IconArrowRight,
    translations: () => translations,
    CTA_TYPES_FOR_TRACKING: () => CTA_TYPES_FOR_TRACKING,
    dataSelectorAttribute: () => DATA_SELECTOR_ATTRIBUTE_FOR_SEO,
    pageId() {
      return this.$route.params.slug
    },
    currentParagraph() {
      const { hash } = this.$route
      const defaultCurrent = this.menu[0] ? this.menu[0].id : ''

      if (hash) {
        return hash.split('#')[1] || defaultCurrent
      }

      return defaultCurrent
    },
    recommendationsError() {
      return {
        ...this.paragraphs.reduce(
          (obj, item) => ({
            ...obj,
            [item.id]: false,
          }),
          {},
        ),
      }
    },
    displayBreadcrumb() {
      return !isEmpty(this.breadcrumb)
    },
    displayAuthor() {
      return !isEmpty(this.author)
    },
    displayMenu() {
      return !isEmpty(this.menu)
    },
    hasRelatedArticles() {
      return !isEmpty(this.relatedArticles)
    },
    actionClass() {
      return tw`mb-2 md:mb-0 md:mr-2 md:last:mr-0`
    },
    actionAloneClass() {
      return tw`block m-0 ml-auto`
    },
  },
  methods: {
    updateVote(vote) {
      this.vote = vote
    },
    ourRecoTitle(value) {
      return value || this.$t(translations.ourRecommandations)
    },
    handleClick(ctaType) {
      trackClick({ zone: this.$route.name, name: ctaType })
    },
    hasButtons(buttonsList) {
      return !isEmpty(buttonsList)
    },
    hasOneButton(buttonsList) {
      return buttonsList.length === 1
    },
    buttonFullWidth(buttonsList) {
      return this.hasOneButton(buttonsList) ? 'never' : 'always'
    },
    getActionClass(buttonsList) {
      return this.hasOneButton(buttonsList)
        ? [this.actionClass, this.actionAloneClass]
        : this.actionClass
    },
  },
}
